import type { Host, MetricLatest } from '../api/types';
import { Sparkline } from './Sparkline';
import { StatusDot } from './StatusDot';

interface Props {
  host: Host;
  metrics: MetricLatest[];
}

function tone(name: string, value: number): 'ok' | 'warn' | 'crit' | 'muted' {
  if (name === 'cpu_pct') {
    if (value >= 95) return 'crit';
    if (value >= 80) return 'warn';
    return 'ok';
  }
  if (name === 'mem_pct') {
    if (value >= 95) return 'crit';
    if (value >= 85) return 'warn';
    return 'ok';
  }
  if (name.endsWith('_free_gb')) {
    if (value < 10) return 'crit';
    if (value < 40) return 'warn';
    return 'ok';
  }
  return 'muted';
}

function fmt(name: string, value: number): string {
  if (name.endsWith('_pct')) return `${value.toFixed(1)}%`;
  if (name.endsWith('_free_gb')) return `${value.toFixed(0)} GB`;
  if (name.endsWith('_bps')) {
    if (value > 1_000_000) return `${(value / 1_000_000).toFixed(1)} MB/s`;
    if (value > 1_000) return `${(value / 1_000).toFixed(1)} KB/s`;
    return `${value.toFixed(0)} B/s`;
  }
  return value.toFixed(2);
}

function label(name: string): string {
  if (name === 'cpu_pct') return 'CPU';
  if (name === 'mem_pct') return 'Memory';
  if (name === 'net_rx_bps') return 'Net rx';
  if (name === 'net_tx_bps') return 'Net tx';
  if (name.startsWith('disk_') && name.endsWith('_free_gb')) {
    const volume = name.slice('disk_'.length, -'_free_gb'.length).toUpperCase();
    return `Disk ${volume}: free`;
  }
  return name;
}

const ORDER = [
  'cpu_pct',
  'mem_pct',
  'disk_c_free_gb',
  'disk_e_free_gb',
  'net_rx_bps',
  'net_tx_bps',
];

export function HostInstrument({ host, metrics }: Props) {
  const byName = new Map(metrics.map((m) => [m.name, m]));
  const ordered = [
    ...ORDER.filter((n) => byName.has(n)),
    ...metrics.map((m) => m.name).filter((n) => !ORDER.includes(n)),
  ];

  const worst: 'ok' | 'warn' | 'crit' | 'muted' =
    metrics.length === 0
      ? 'muted'
      : metrics.reduce<'ok' | 'warn' | 'crit'>((acc, m) => {
          const t = tone(m.name, m.value);
          if (t === 'crit') return 'crit';
          if (t === 'warn' && acc !== 'crit') return 'warn';
          return acc;
        }, 'ok');

  const borderClass =
    worst === 'crit'
      ? 'border-cockpit-crit'
      : worst === 'warn'
        ? 'border-cockpit-warn'
        : 'border-cockpit-border';

  return (
    <section className={`rounded-lg border ${borderClass} bg-cockpit-panel p-4`}>
      <header className="mb-3 flex items-center gap-2">
        <StatusDot tone={worst} pulse={worst === 'crit'} />
        <h3 className="text-sm font-semibold uppercase tracking-wider">{host.name}</h3>
        <span className="ml-auto text-xs uppercase tracking-wider text-cockpit-dim">
          {host.env}
        </span>
      </header>
      {metrics.length === 0 ? (
        <p className="text-sm text-cockpit-dim">
          no metrics yet — waiting for first collector tick
        </p>
      ) : (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 lg:grid-cols-3">
          {ordered.map((name) => {
            const m = byName.get(name);
            if (!m) return null;
            const t = tone(m.name, m.value);
            return (
              <div key={name} className="flex flex-col">
                <dt className="text-xs uppercase tracking-wider text-cockpit-dim">
                  {label(m.name)}
                </dt>
                <dd
                  className={
                    t === 'crit'
                      ? 'text-lg font-semibold text-cockpit-crit'
                      : t === 'warn'
                        ? 'text-lg font-semibold text-cockpit-warn'
                        : 'text-lg font-semibold text-cockpit-text'
                  }
                >
                  {fmt(m.name, m.value)}
                </dd>
                <Sparkline hostId={host.id} name={m.name} env={host.env} tone={t} />
              </div>
            );
          })}
        </dl>
      )}
    </section>
  );
}
